/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Button } from '@carbon/react';

import { mapClusterInfo } from 'App/Pages/Diagram/features/deployment/actions/map-cluster-info';
import { StateIndicator } from 'App/Pages/Diagram/features/deployment/actions/state-indicator';
import { deploymentTypes, deploymentStates as states } from 'App/Pages/Diagram/features/deployment/utils';
import { notificationStore } from 'components/NotificationSystem';
import { formLinkStore } from 'App/Pages/Diagram/FormLinking';

import deploymentService from './DeploymentService';

export default function Deploy({
  selectedCluster,
  tenantId,
  clientId,
  clientSecret,
  diagramId,
  processApplicationId,
  processApplicationVersionId,
  closeModal,
  deploymentType,
  currentState,
  transitionToDeployingState,
  transitionToDeploymentFailedState,
  disabled,
  isInputValidationSuccessful,
  enableValidation
}) {
  const deploy = async () => {
    if (selectedCluster?.authentication === 'CLIENT_CREDENTIALS' && !isInputValidationSuccessful) {
      enableValidation();
      return;
    }

    const clusterInfo = mapClusterInfo(selectedCluster, tenantId, clientId, clientSecret);

    try {
      transitionToDeployingState();
      if (deploymentType === deploymentTypes.DIAGRAM) {
        await deploymentService.deployDiagram(diagramId, clusterInfo, formLinkStore.formResolutions);
      } else if (deploymentType === deploymentTypes.PROCESS_APPLICATION) {
        if (processApplicationVersionId) {
          await deploymentService.deployProcessApplicationVersion(
            processApplicationVersionId,
            clusterInfo,
            formLinkStore.formResolutions
          );
        } else {
          await deploymentService.deployProcessApplication(
            processApplicationId,
            clusterInfo,
            formLinkStore.formResolutions
          );
        }
      }
      notificationStore.showSuccess(getSuccessMessage(deploymentType));
      closeModal();
    } catch (e) {
      transitionToDeploymentFailedState(e?.[0]?.detail ?? '');
    }
  };

  const renderStateIndicator = () => {
    switch (currentState) {
      case states.DEPLOYMENT_FAILED:
        return <StateIndicator text="Failed" status="error" />;
      case states.DEPLOYING:
        return <StateIndicator text="Deploying" status="active" />;
      default:
        return null;
    }
  };
  return (
    <>
      {renderStateIndicator() || (
        <Button onClick={deploy} type="submit" data-modal-primary-focus disabled={disabled}>
          Deploy
        </Button>
      )}
    </>
  );
}

const getSuccessMessage = (deploymentType) => {
  if (deploymentType === deploymentTypes.DIAGRAM) {
    return 'Diagram deployed!';
  } else if (deploymentType === deploymentTypes.PROCESS_APPLICATION) {
    return 'Process application deployed!';
  }

  return '';
};
